import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { money, STRIPE_TEST_MODE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PostcardCanvas from "@/components/PostcardCanvas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { TermsAgree, TERMS_VERSION } from "@/components/TermsAgree";
import { Loader2, MapPin, ArrowLeft } from "lucide-react";

const TYPE_LABEL = { premium: "4×9 Premium Hero", standard: "4×6 Standard", basic: "4×3 Basic" };

export default function PostcardDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, openAuth } = useAuth();
  const [run, setRun] = useState(null);
  const [myBiz, setMyBiz] = useState([]);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ business_id: "", deal_headline: "", deal_details: "", tos: false });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/postcard-runs/${id}`).then(({ data }) => setRun(data)), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onVisible = () => { if (!document.hidden) load(); };
    window.addEventListener("focus", load);
    document.addEventListener("visibilitychange", onVisible);
    const t = setInterval(load, 20000);
    return () => {
      window.removeEventListener("focus", load);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(t);
    };
  }, [load]);
  useEffect(() => { if (user) api.get("/my/businesses").then(({ data }) => setMyBiz(data.filter((b) => b.listing_status === "approved"))); }, [user]);

  const onSelect = (s) => {
    if (!user) return openAuth("owner");
    if (myBiz.length === 0) { toast.error("Add an approved business first from your dashboard."); return navigate("/dashboard"); }
    setSlot(s);
    setForm({ business_id: myBiz[0].id, deal_headline: "", deal_details: "", tos: false });
  };

  const checkout = async () => {
    if (!form.business_id) return toast.error("Choose a business");
    if (!form.tos) return toast.error("Please accept the Terms of Service to continue");
    setBusy(true);
    try {
      const { data } = await api.post("/payments/postcard-slot", {
        slot_id: slot.id, business_id: form.business_id,
        deal_headline: form.deal_headline, deal_details: form.deal_details,
        terms_accepted: true, terms_version: TERMS_VERSION,
        origin_url: window.location.origin,
        affiliate_id: window.affiliateId || undefined,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  if (!run) return <div className="h-[60vh] grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sam-crimson" /></div>;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
      <button onClick={() => navigate("/postcards")} className="flex items-center gap-1 text-sm font-semibold text-sam-slate hover:text-sam-navy mb-4"><ArrowLeft className="h-4 w-4" />All routes</button>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <span className="font-mono text-xs text-sam-slate">{run.route_code}</span>
          <h1 className="font-display text-3xl font-extrabold text-sam-navy flex items-center gap-2"><MapPin className="h-6 w-6 text-sam-crimson" />{run.city}, {run.state_abbr}</h1>
          <p className="text-sam-slate">{run.metro} · {(run.mailer_count || 0).toLocaleString()} homes mailed</p>
        </div>
        <Badge className="bg-sam-green text-base px-3 py-1">{(run.slots || []).filter((s) => s.status === "available").length} of 12 slots open</Badge>
      </div>

      <div className="grid lg:grid-cols-[1fr_300px] gap-8">
        <PostcardCanvas slots={run.slots} onSelect={onSelect} selectable run={run} />
        <aside className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-5">
            <h3 className="font-display font-bold text-sam-navy mb-3">How it works</h3>
            <ol className="space-y-3 text-sm text-sam-slate list-decimal list-inside">
              <li>Tap an open slot on the postcard.</li>
              <li>Pick your business and write your deal + QR coupon headline.</li>
              <li>Pay securely — your ad is locked to that slot.</li>
            </ol>
            <div className="mt-4 space-y-1.5 text-sm">
              {["premium", "standard", "basic"].map((t) => {
                const s = (run.slots || []).find((x) => x.slot_type === t);
                const price = s ? s.price : { premium: 1800, standard: 1300, basic: 900 }[t];
                return <div key={t} className="flex justify-between"><span className="text-sam-slate">{TYPE_LABEL[t]}</span><span className="font-mono font-bold text-sam-navy">{money(price)}</span></div>;
              })}
            </div>
          </div>
          {!user && <Button className="w-full bg-sam-crimson hover:bg-sam-crimson/90" onClick={() => openAuth("owner")}>Log in to reserve a slot</Button>}
        </aside>
      </div>

      <Dialog open={!!slot} onOpenChange={(o) => !o && setSlot(null)}>
        <DialogContent data-testid="reserve-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Reserve {slot && TYPE_LABEL[slot.slot_type]} — {slot && money(slot.price)}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Business</Label>
              <Select value={form.business_id} onValueChange={(v) => setForm({ ...form, business_id: v })}>
                <SelectTrigger data-testid="reserve-business-select"><SelectValue placeholder="Choose your business" /></SelectTrigger>
                <SelectContent>{myBiz.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Deal headline</Label><Input value={form.deal_headline} onChange={(e) => setForm({ ...form, deal_headline: e.target.value })} placeholder="e.g. $50 off any repair" data-testid="reserve-headline" /></div>
            <div><Label>Deal details</Label><Textarea value={form.deal_details} onChange={(e) => setForm({ ...form, deal_details: e.target.value })} placeholder="Fine print, e.g. Mention Saving America Money." data-testid="reserve-details" /></div>
            <TermsAgree checked={form.tos} onChange={(v) => setForm({ ...form, tos: v })} testid="reserve-tos" />
            <Button className="w-full bg-sam-crimson hover:bg-sam-crimson/90" onClick={checkout} disabled={busy || !form.tos} data-testid="reserve-pay-btn">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Pay {slot && money(slot.price)} & Reserve
            </Button>
            {STRIPE_TEST_MODE && <p className="text-xs text-center text-muted-foreground">Test card 4242 4242 4242 4242 · any future date · any CVC</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
